'use client';

import { termsConfig } from '@/features/terms/terms.config';
import { MainLayout } from '@/layouts/main';
import { useT } from 'next-i18next/client';
import { generateNextSeo } from 'next-seo/pages';
import Head from 'next/head';
import type { FC } from 'react';

type TermsPageProps = {
  locale: string;
};

export const TermsPage: FC<TermsPageProps> = ({ locale }) => {
  const { t } = useT(termsConfig.i18nNamespaces);

  return (
     <>
       <Head>
         {generateNextSeo({
          title: `${t('terms:page.title')} - ${locale}`,
          description: t('terms:page.description'),
         })}
       </Head>
       <MainLayout>
         <div className="bg-[#0A0A0A]">
           <div className="mx-auto max-w-4xl px-4 py-8">
             <div className="mb-8 text-center">
               <h1 className="text-4xl font-bold text-white md:text-5xl">
                 {t('terms:page.title')}
               </h1>
               <p className="mt-2 text-sm text-gray-400 italic">
                 {t('terms:lastUpdated')}
               </p>
             </div>
             <div className="space-y-8">
               {/* Section 1 */}
               <section className="space-y-4">
                 <h2 className="text-2xl font-bold text-white">
                   {t('terms:section_01_title')}
                 </h2>
                 <p className="leading-relaxed text-gray-300">
                   {t('terms:section_01_intro')}
                 </p>
               </section>

               {/* Section 2 */}
               <section className="space-y-4">
                 <h2 className="text-2xl font-bold text-white">
                   {t('terms:section_02_title')}
                 </h2>
                 <p className="leading-relaxed text-gray-300">
                   {t('terms:section_02_intro')}
                 </p>
                 <ul className="space-y-2">
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_02_list_0')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_02_list_1')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_02_list_2')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_02_list_3')}
                   </li>
                 </ul>
               </section>

               {/* Section 3 */}
               <section className="space-y-4">
                 <h2 className="text-2xl font-bold text-white">
                   {t('terms:section_03_title')}
                 </h2>
                 <p className="leading-relaxed text-gray-300">
                   {t('terms:section_03_intro')}
                 </p>
                 <ul className="space-y-2">
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_03_list_0')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_03_list_1')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_03_list_2')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_03_list_3')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_03_list_4')}
                   </li>
                 </ul>
               </section>

               {/* Section 4 */}
               <section className="space-y-4">
                 <h2 className="text-2xl font-bold text-white">
                   {t('terms:section_04_title')}
                 </h2>
                 <p className="leading-relaxed text-gray-300">
                   {t('terms:section_04_intro')}
                 </p>
                 <ul className="space-y-2">
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_04_list_0')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_04_list_1')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_04_list_2')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_04_list_3')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_04_list_4')}
                   </li>
                 </ul>
               </section>

               {/* Section 5 */}
               <section className="space-y-4">
                 <h2 className="text-2xl font-bold text-white">
                   {t('terms:section_05_title')}
                 </h2>
                 <p className="leading-relaxed text-gray-300">
                   {t('terms:section_05_intro')}
                 </p>
               </section>

               {/* Section 6 */}
               <section className="space-y-4">
                 <h2 className="text-2xl font-bold text-white">
                   {t('terms:section_06_title')}
                 </h2>
                 <p className="leading-relaxed text-gray-300">
                   {t('terms:section_06_intro')}
                 </p>
                 <ul className="space-y-2">
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_06_list_0')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_06_list_1')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_06_list_2')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_06_list_3')}
                   </li>
                 </ul>
               </section>

               {/* Section 7 */}
               <section className="space-y-4">
                 <h2 className="text-2xl font-bold text-white">
                   {t('terms:section_07_title')}
                 </h2>
                 <p className="leading-relaxed text-gray-300">
                   {t('terms:section_07_intro')}
                 </p>
                 <ul className="space-y-2">
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_07_list_0')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_07_list_1')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_07_list_2')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_07_list_3')}
                   </li>
                 </ul>
               </section>

               {/* Section 8 */}
               <section className="space-y-4">
                 <h2 className="text-2xl font-bold text-white">
                   {t('terms:section_08_title')}
                 </h2>
                 <p className="leading-relaxed text-gray-300">
                   {t('terms:section_08_intro')}
                 </p>
                 <ul className="space-y-2">
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_08_list_0')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_08_list_1')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_08_list_2')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_08_list_3')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_08_list_4')}
                   </li>
                 </ul>
               </section>

               {/* Section 9 */}
               <section className="space-y-4">
                 <h2 className="text-2xl font-bold text-white">
                   {t('terms:section_09_title')}
                 </h2>
                 <p className="leading-relaxed text-gray-300">
                   {t('terms:section_09_intro')}
                 </p>
                 <ul className="space-y-2">
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_09_list_0')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_09_list_1')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_09_list_2')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_09_list_3')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_09_list_4')}
                   </li>
                 </ul>
               </section>

               {/* Section 10 */}
               <section className="space-y-4">
                 <h2 className="text-2xl font-bold text-white">
                   {t('terms:section_10_title')}
                 </h2>
                 <p className="leading-relaxed text-gray-300">
                   {t('terms:section_10_intro')}
                 </p>
                 <ul className="space-y-2">
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_10_list_0')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_10_list_1')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_10_list_2')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_10_list_3')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_10_list_4')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_10_list_5')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_10_list_6')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_10_list_7')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_10_list_8')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_10_list_9')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_10_list_10')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_10_list_11')}
                   </li>
                 </ul>
               </section>

               {/* Section 11 */}
               <section className="space-y-4">
                 <h2 className="text-2xl font-bold text-white">
                   {t('terms:section_11_title')}
                 </h2>
                 <p className="leading-relaxed text-gray-300">
                   {t('terms:section_11_intro')}
                 </p>
                 <ul className="space-y-2">
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_11_list_0')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_11_list_1')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_11_list_2')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_11_list_3')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_11_list_4')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_11_list_5')}
                   </li>
                 </ul>
               </section>

               {/* Section 12 */}
               <section className="space-y-4">
                 <h2 className="text-2xl font-bold text-white">
                   {t('terms:section_12_title')}
                 </h2>
                 <p className="leading-relaxed text-gray-300">
                   {t('terms:section_12_intro')}
                 </p>
                 <ul className="space-y-2">
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_12_list_0')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_12_list_1')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_12_list_2')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_12_list_3')}
                   </li>
                   <li className="flex items-start text-gray-300">
                     <span className="mr-2 text-white">•</span>
                     {t('terms:section_12_list_4')}
                   </li>
                 </ul>
               </section>

               {/* Section 13 */}
               <section className="space-y-4">
                 <h2 className="text-2xl font-bold text-white">
                   {t('terms:section_13_title')}
                 </h2>
                 <p className="leading-relaxed text-gray-300">
                   {t('terms:section_13_intro')}
                 </p>
               </section>

               {/* Section 14 */}
               <section className="space-y-4">
                 <h2 className="text-2xl font-bold text-white">
                   {t('terms:section_14_title')}
                 </h2>
                 <p className="leading-relaxed text-gray-300">
                   {t('terms:section_14_intro')}
                 </p>
               </section>

               {/* Section 15 */}
               <section className="space-y-4">
                 <h2 className="text-2xl font-bold text-white">
                   {t('terms:section_15_title')}
                 </h2>
                 <p className="leading-relaxed text-gray-300">
                   {t('terms:section_15_intro')}
                 </p>
               </section>

               {/* Section 16 */}
               <section className="space-y-4">
                 <h2 className="text-2xl font-bold text-white">
                   {t('terms:section_16_title')}
                 </h2>
                 <p className="leading-relaxed text-gray-300">
                   {t('terms:section_16_intro')}
                 </p>
               </section>

               {/* Section 17 */}
               <section className="space-y-4">
                 <h2 className="text-2xl font-bold text-white">
                   {t('terms:section_17_title')}
                 </h2>
                 <p className="leading-relaxed text-gray-300">
                   {t('terms:section_17_intro')}
                 </p>
                 <p className="text-blue-400">
                   {t('terms:contactEmail')}:{' '}
                   <a
                      href="mailto:nookai@embira.co"
                      className="underline text-blue-400 hover:text-blue-300"
                   >
                      nookai@embira.co
                   </a>
                 </p>
               </section>
             </div>
           </div>
         </div>
       </MainLayout>
     </>
   );
};